import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import ComponentContainer from '@/components/component-container/ComponentContainer';

type StepStatus = 'done' | 'active' | 'pending';

type Step = {
	title: string;
	description: string;
	status: StepStatus;
};

const steps: Step[] = [
	{
		title: 'Verificación de identidad',
		description: 'Completado',
		status: 'done',
	},
	{
		title: 'Revisión por un operador',
		description: 'En curso',
		status: 'active',
	},
	{
		title: 'Envío de documentos adicionales por correo',
		description: 'Aplica solamente para certificados de representante legal, persona jurídica, colegiado profesional, funcionario público',
		status: 'pending',
	},
	{
		title: 'Firmar documentos',
		description: '',
		status: 'pending',
	},
];

const StepIcon = ({ status }: { status: StepStatus }) => (
	<div
		className={cn(
			'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2',
			status === 'done' && 'border-primary bg-primary text-primary-foreground',
			status === 'active' && 'border-primary bg-primary/15',
			status === 'pending' && 'border-border bg-background',
		)}
	>
		{status === 'done' && <Check className="h-4 w-4" strokeWidth={3} />}
		{status === 'active' && (
			<div className="h-3 w-3 rounded-full bg-primary" />
		)}
	</div>
);

const VerificationStatus = () => {
	return (
		<ComponentContainer>
			<div
				className="flex min-h-[70vh] items-center justify-center"
				style={{ padding: '16px 16px 48px' }}
			>
				<div
					className="w-full max-w-lg rounded-lg border border-border bg-card shadow-sm"
					style={{ padding: 32 }}
				>
					<p className="text-sm font-semibold tracking-wide text-primary uppercase">
						Verificación de identidad
					</p>
					<h1
						className="font-bold text-foreground"
						style={{ fontSize: '28px', fontWeight: 700, marginTop: 8 }}
					>
						Recibimos tu información
					</h1>
					<p
						className="text-muted-foreground"
						style={{ marginTop: 12 }}
					>
						Tu proceso de verificación (KYC) fue enviado correctamente.
						Faltan estos pasos para que puedas empezar a firmar
					</p>

					<div className="flex flex-col" style={{ marginTop: 32 }}>
						{steps.map((step, index) => (
							<div key={step.title} className="flex gap-4">
								<div className="flex flex-col items-center">
									<StepIcon status={step.status} />
									{index < steps.length - 1 && (
										<div
											className={cn(
												'w-0.5 flex-1 min-h-8',
												step.status === 'done'
													? 'bg-primary'
													: 'bg-border',
											)}
										/>
									)}
								</div>
								<div style={{ paddingBottom: 32 }}>
									<p
										className={cn(
											'font-semibold',
											step.status === 'pending'
												? 'text-muted-foreground'
												: 'text-foreground',
										)}
									>
										{step.title}
									</p>
									<p
										className={cn(
											'text-sm',
											step.status === 'active'
												? 'text-primary'
												: 'text-muted-foreground',
										)}
									>
										{step.description}
									</p>
								</div>
							</div>
						))}
					</div>

					<div
						className="flex items-center gap-3 rounded-md bg-primary/10 text-sm text-primary"
						style={{ marginTop: 24, padding: '12px 16px' }}
					>
						<div className="h-4 w-4 shrink-0 rounded-full border-2 border-primary" />
						<span>
							Tiempo estimado de espera: <strong>hasta 1 hora</strong>.
						</span>
					</div>

					<hr style={{ margin: '24px 0' }} />

					<p className="text-sm text-muted-foreground">
						¿Tienes dudas sobre tu proceso? Escríbenos a{' '}
						<a
							href="mailto:contacto@cybersign.gt"
							className="text-primary hover:underline"
						>
							contacto@cybersign.gt
						</a>
						.
					</p>
				</div>
			</div>
		</ComponentContainer>
	);
};

export default VerificationStatus;
